import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';

interface StatCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  trend?: number;
  percentage?: number;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'info';
  isPercentage?: boolean;
}

/**
 * StatCard component for displaying a statistic with optional trend indicator
 *
 * @param {Object} props - The component props
 * @param {string} props.title - The title of the stat
 * @param {number|string} props.value - The main value to display
 * @param {string} [props.subtitle] - Optional subtitle text
 * @param {number} [props.trend] - Optional trend percentage (positive or negative)
 * @param {number} [props.percentage] - Optional percentage to display in a separate box
 * @param {string} [props.color='primary'] - Color theme for the card
 * @param {boolean} [props.isPercentage=false] - Whether to display value as percentage
 * @returns {JSX.Element} The StatCard component
 */
const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  trend,
  percentage,
  color = 'primary',
  isPercentage = false,
}) => {
  const theme = useTheme();
  const colorMap = {
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
  };

  const bgColor = alpha(colorMap[color], 0.08);

  return (
    <Card
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${bgColor} 0%, ${alpha(colorMap[color], 0.02)} 100%)`,
        border: `1px solid ${alpha(colorMap[color], 0.2)}`,
        borderRadius: 3,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px ${alpha(colorMap[color], 0.2)}`,
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 1,
            fontWeight: 500,
          }}
        >
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: colorMap[color],
              lineHeight: 1,
            }}
          >
            {typeof value === 'number' ? value.toLocaleString('es-CL') : value}
            {isPercentage && '%'}
          </Typography>
          {trend !== undefined && trend !== 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: trend > 0 ? 'success.main' : 'error.main',
                bgcolor: trend > 0 ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
                px: 1,
                py: 0.5,
                borderRadius: 2,
              }}
            >
              {trend > 0 ? <TrendingUpIcon fontSize="small" /> : <TrendingDownIcon fontSize="small" />}
              <Typography variant="body2" sx={{ fontWeight: 600, ml: 0.5 }}>
                {Math.abs(trend).toFixed(1)}%
              </Typography>
            </Box>
          )}
          {percentage !== undefined && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: colorMap[color],
                bgcolor: alpha(colorMap[color], 0.1),
                px: 1,
                py: 0.5,
                borderRadius: 2,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {percentage.toFixed(1)}%
              </Typography>
            </Box>
          )}
        </Box>
        {subtitle && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mt: 1,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default StatCard;
